import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, CheckCircle2, Phone, Mail, User, Building2, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm({ formData, totals, quoteId }) {
  const { language } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('lead.required', language);
    if (!form.email.trim()) errs.email = t('lead.required', language);
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t('lead.invalidEmail', language);
    if (!form.phone.trim()) errs.phone = t('lead.required', language);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company_name: form.company.trim(),
        message: form.message.trim(),
        quote_id: quoteId || '',
        accounting_form: formData.accountingForm,
        legal_entity: formData.legalEntity,
        monthly_total: totals.monthlyTotal,
        language,
      });
      setSent(true);
    } catch {
      toast.error(t('common.error', language));
    } finally {
      setSending(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (sent) {
    return (
      <div className="bg-[#1B3A7B] rounded-2xl p-8 sm:p-10 text-center" data-testid="lead-success">
        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-white mb-2">
          {t('lead.successTitle', language)}
        </h3>
        <p className="text-blue-200 text-base">
          {t('lead.successMsg', language)}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#1B3A7B] rounded-2xl p-8 sm:p-10" data-testid="lead-form-section">
      <div className="text-center mb-8">
        <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2">
          {t('lead.ctaTitle', language)}
        </h3>
        <p className="text-blue-200 text-base max-w-lg mx-auto">
          {t('lead.ctaSubtitle', language)}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-4">
        {/* Name */}
        <div>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
            <Input
              placeholder={t('lead.name', language)}
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={`h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:border-[#E8941A] focus:ring-[#E8941A] ${errors.name ? 'border-red-400' : ''}`}
              data-testid="lead-name-input"
            />
          </div>
          {errors.name && <p className="text-red-400 text-xs mt-1 font-medium">{errors.name}</p>}
        </div>

        {/* Email + Phone row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
              <Input
                type="email"
                placeholder={t('lead.email', language)}
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className={`h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:border-[#E8941A] focus:ring-[#E8941A] ${errors.email ? 'border-red-400' : ''}`}
                data-testid="lead-email-input"
              />
            </div>
            {errors.email && <p className="text-red-400 text-xs mt-1 font-medium">{errors.email}</p>}
          </div>
          <div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
              <Input
                type="tel"
                placeholder={t('lead.phone', language)}
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className={`h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:border-[#E8941A] focus:ring-[#E8941A] ${errors.phone ? 'border-red-400' : ''}`}
                data-testid="lead-phone-input"
              />
            </div>
            {errors.phone && <p className="text-red-400 text-xs mt-1 font-medium">{errors.phone}</p>}
          </div>
        </div>

        {/* Company */}
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
          <Input
            placeholder={t('lead.company', language)}
            value={form.company}
            onChange={(e) => updateField('company', e.target.value)}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:border-[#E8941A] focus:ring-[#E8941A]"
            data-testid="lead-company-input"
          />
        </div>

        {/* Message */}
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
          <textarea
            placeholder={t('lead.messagePlaceholder', language)}
            value={form.message}
            onChange={(e) => updateField('message', e.target.value)}
            rows={3}
            className="flex w-full rounded-md border px-3 py-2 text-sm pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:border-[#E8941A] focus:ring-[#E8941A] focus:outline-none resize-none"
            data-testid="lead-message-input"
          />
        </div>

        {/* Consent */}
        <div className="flex items-start gap-3">
          <Checkbox
            checked={consent}
            onCheckedChange={setConsent}
            className="mt-0.5 border-white/30 data-[state=checked]:bg-[#E8941A] data-[state=checked]:border-[#E8941A]"
            data-testid="lead-consent-checkbox"
          />
          <label className="text-xs text-blue-200 leading-relaxed cursor-pointer" onClick={() => setConsent(!consent)}>
            {t('lead.consent', language)}
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!consent || sending}
          className={`w-full h-13 py-3.5 rounded-md font-semibold text-base flex items-center justify-center gap-2.5 transition-all ${
            consent && !sending
              ? 'bg-[#E8941A] hover:bg-[#d68515] text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
          data-testid="lead-submit-btn"
        >
          <Send className="w-4 h-4" />
          {sending ? t('lead.sending', language) : t('lead.submit', language)}
        </button>
      </form>
    </div>
  );
}
